import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

const apiUrl = process.env.REACT_APP_API_URL;

function ManageEmails() {
  const navigate = useNavigate();
  const location = useLocation();
  const [emails, setEmails] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");
  const [role, setRole] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [usersList, setUsersList] = useState([]);

  const emailsPerPage = 15;

  useEffect(() => {
    fetchUsersList(); // Add this line
    const searchParams = new URLSearchParams(location.search);
    const urlFromDate = searchParams.get("fromDate");
    const urlToDate = searchParams.get("toDate");

    const user = JSON.parse(localStorage.getItem("user"));
    setRole(user.role);

    if (urlFromDate) {
      setFromDate(urlFromDate);
      setTempFromDate(urlFromDate);
    }
    if (urlToDate) {
      setToDate(urlToDate);
      setTempToDate(urlToDate);
    }
  }, [location.search]);

  useEffect(() => {
    fetchEmails();
  }, [currentPage, fromDate, toDate]);

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const updateURLParams = (newFromDate, newToDate) => {
    const searchParams = new URLSearchParams(location.search);

    if (newFromDate) {
      searchParams.set("fromDate", newFromDate);
    } else {
      searchParams.delete("fromDate");
    }

    if (newToDate) {
      searchParams.set("toDate", newToDate);
    } else {
      searchParams.delete("toDate");
    }

    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const fetchUsersList = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${apiUrl}/get-users-list`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        const users = responseData?.data?.users || [];
        console.log(users, "users");
        setUsersList(users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      let url = `${apiUrl}fetch-email-templates?searchString=${searchString}&status=${status}&page=${currentPage}&userSearch=${selectedUser}`;

      if (fromDate) url += `&fromDate=${fromDate}`;
      if (toDate) url += `&toDate=${toDate}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails);
        setTotalPages(Math.ceil(data.count / emailsPerPage));
      } else {
        console.error("Failed to fetch email details");
      }
    } catch (error) {
      console.error("Error fetching email templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePresetSelect = (preset) => {
    let newFromDate, newToDate;

    switch (preset) {
      case "this-week": {
        const curr = new Date();
        const first =
          curr.getDate() - curr.getDay() + (curr.getDay() === 0 ? -6 : 1);
        newFromDate = new Date(curr.setDate(first));
        newToDate = new Date(curr);
        newToDate.setDate(first + 6);
        break;
      }
      case "last-week": {
        const curr = new Date();
        
        // Calculate last week's Monday
        const lastMonday = new Date(curr);
        lastMonday.setDate(curr.getDate() - (curr.getDay() || 7) - 6);
        newFromDate = lastMonday;
        
        // Get Sunday by adding 6 days to Monday
        newToDate = new Date(lastMonday);
        newToDate.setDate(lastMonday.getDate() + 6);
        break;
      }

      case "this-month": {
        const curr = new Date();
        newFromDate = new Date(curr.getFullYear(), curr.getMonth(), 1);
        newToDate = new Date(curr.getFullYear(), curr.getMonth() + 1, 0);
        break;
      }
      case "last-month": {
        const curr = new Date();
        newFromDate = new Date(curr.getFullYear(), curr.getMonth() - 1, 1);
        newToDate = new Date(curr.getFullYear(), curr.getMonth(), 0);
        break;
      }
      default:
        return;
    }

    const formattedFromDate = formatDate(newFromDate);
    const formattedToDate = formatDate(newToDate);

    setFromDate(formattedFromDate);
    setToDate(formattedToDate);
    setTempFromDate(formattedFromDate);
    setTempToDate(formattedToDate);
    updateURLParams(formattedFromDate, formattedToDate);
    setCurrentPage(1);
  };

  const handleTempDateChange = (event, type) => {
    const value = event.target.value;
    if (type === "from") {
      setTempFromDate(value);
    } else {
      setTempToDate(value);
    }
  };

  const handleDateSelection = (type) => {
    if (type === "from") {
      if (toDate && new Date(tempFromDate) > new Date(toDate)) {
        alert(
          "The 'From Date' must be earlier than the 'To Date'. Please correct your selection."
        );
        setTempFromDate(fromDate);
        return;
      }
      setFromDate(tempFromDate);
      updateURLParams(tempFromDate, toDate);
    } else {
      if (fromDate && new Date(fromDate) > new Date(tempToDate)) {
        alert(
          "The 'To Date' must be later than the 'From Date'. Please correct your selection."
        );
        setTempToDate(toDate);
        return;
      }
      setToDate(tempToDate);
      updateURLParams(fromDate, tempToDate);
    }
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setFromDate("");
    setToDate("");
    setTempFromDate("");
    setTempToDate("");
    updateURLParams(null, null);
    setCurrentPage(1);
  };

  const handleClear = () => {
    setSearchString("");
    setStatus("");
    setSelectedUser("");
    setFromDate("");
    setToDate("");
    setTempFromDate("");
    setTempToDate("");
    setCurrentPage(1);
    updateURLParams(null, null);
    fetchEmailsAfterClear();
  };

  useEffect(() => {
    fetchEmails(); // Fetch emails on component mount
  }, [currentPage]);

  const handleCreateEmail = (e) => {
    e.preventDefault();
    navigate("/create-email");
  };

  const handleViewEmail = (id) => (e) => {
    e.preventDefault();
    navigate(`/view-email/${id}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
    fetchEmails();
  };

  const fetchEmailsAfterClear = async () => {
    setLoading(true); // Start loading
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${apiUrl}fetch-email-templates?searchString=&status=&page=1`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setEmails(data.emails);
        setTotalPages(Math.ceil(data.count / emailsPerPage));
      } else {
        console.error("Failed to fetch email details");
      }
    } catch (error) {
      console.error("Error fetching email templates:", error);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleDeleteEmail = async (id) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${apiUrl}delete-email-template/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        setEmails(emails.filter((email) => email._id !== id));
      } else {
        console.error("Failed to delete email");
      }
    } catch (error) {
      console.error("Error deleting email template:", error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (role === "admin") {
    return (
      <>
        <div className="dashboard-title mb-3">
          <h1>Manage Emails</h1>
          <button
            type="button"
            onClick={handleCreateEmail}
            className="btn btn-primary"
          >
            Create new Email
          </button>
        </div>

        <div
          className="rounded p-4"
          style={{ backgroundColor: "rgb(15, 46, 94)" }}
        >
          <div className="d-flex align-items-center gap-3">
            {/* Filter Icon */}
            <div className="text-white">
              <i className="bi bi-funnel-fill fs-5"></i>
            </div>

            {/* Dropdowns and Search Container */}
            <div className="d-flex gap-3 flex-grow-1">
              {/* Status Dropdown */}
              <div style={{ minWidth: "180px" }}>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Choose Status</option>
                  <option value="Sent">Sent</option>
                  <option value="Draft">Draft</option>
                  <option value="In Outlook">In Outlook</option>
                </select>
              </div>

              {/* User Dropdown */}
              <div style={{ minWidth: "180px" }}>
                <select
                  className="form-select"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                >
                  <option value="">Select User</option>
                  {usersList.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.userName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Search Input */}
              <div className="flex-grow-1">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  value={searchString}
                  onChange={(e) => setSearchString(e.target.value)}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={handleSearch}
              >
                Search
              </button>

              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={handleClear}
              >
                Clear
              </button>

              {/* Date Filter Dropdown */}
              <div className="dropdown">
                <button
                  className="btn btn-secondary dropdown-toggle px-4"
                  type="button"
                  id="dateFilterDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-calendar me-2"></i>
                  Date Filter
                </button>
                <ul
                  className="dropdown-menu p-3"
                  aria-labelledby="dateFilterDropdown"
                >
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handlePresetSelect("this-week")}
                    >
                      This Week
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handlePresetSelect("last-week")}
                    >
                      Last Week
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handlePresetSelect("this-month")}
                    >
                      This Month
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handlePresetSelect("last-month")}
                    >
                      Last Month
                    </button>
                  </li>
                  <hr className="my-2" />
                  <li className="px-2">
                    <div className="mb-2">
                      <label className="form-label">From Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={tempFromDate}
                        onChange={(e) => handleTempDateChange(e, "from")}
                        onBlur={() => handleDateSelection("from")}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label">To Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={tempToDate}
                        onChange={(e) => handleTempDateChange(e, "to")}
                        onBlur={() => handleDateSelection("to")}
                      />
                    </div>
                    <div className="mt-3">
                      <button
                        className="btn btn-secondary w-100"
                        onClick={handleClearDates}
                      >
                        Clear Dates
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center mt-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {emails.length > 0 ? (
              <div className="table-responsive mt-4">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Email template name</th>
                      <th scope="col">Type</th>
                      <th scope="col">Created by name</th>
                      <th scope="col">Created date</th>
                      <th scope="col">Status</th>
                      <th scope="col">Action items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map((email, index) => (
                      <tr key={email._id}>
                        <th scope="row">
                          {(currentPage - 1) * emailsPerPage + index + 1}
                        </th>
                        <td>{email.emailName}</td>
                        <td>
                          <span className={`badge ${
                            email.emailName && email.emailName.includes('E&S') ? 'bg-warning text-dark' : 'bg-primary'
                          }`}>
                            {email.emailName && email.emailName.includes('E&S') ? 'E&S' : 'Standard'}
                          </span>
                        </td>
                        <td>{email.userName}</td>
                        <td>
                          {new Date(email.createdAt).toLocaleDateString()}
                        </td>
                        <td>{email.status}</td>
                        <td>
                          <a href="#" onClick={handleViewEmail(email._id)}>
                            <i className="bi bi-eye me-2"></i>
                          </a>
                          {email.status !== "Sent" &&
                            email.status !== "In Outlook" && (
                              <i
                                className="bi bi-trash"
                                onClick={() => handleDeleteEmail(email._id)}
                                style={{ cursor: "pointer" }}
                              ></i>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="d-flex justify-content-between align-items-center mt-4">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-center mt-4">No emails to display</p>
            )}
          </>
        )}
      </>
    );
  } else {
    return (
      <>
        <div className="dashboard-title mb-3">
          <h1>Manage Emails</h1>
          <button
            type="button"
            onClick={handleCreateEmail}
            className="btn btn-primary"
          >
            Create new Email
          </button>
        </div>
        <div className="row mb-3 clearfix align-items-center">
          <div className="col-12 col-lg-10 v-space">
            <div className="filters d-flex align-items-center h-100">
              <div className="row clearfix w-100 align-items-center">
                <div className="col-12 col-lg-2 col-xl-1">
                  <i className="bi bi-funnel-fill"></i>
                </div>
                <div className="col-12 col-lg-3 col-xl-3">
                  <select
                    className="form-select v-space"
                    aria-label="Default select example"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="">Choose Status</option>
                    <option value="Sent">Sent</option>
                    <option value="Draft">Draft</option>
                    <option value="In Outlook">In Outlook</option>
                  </select>
                </div>
                <div className="col-12 col-lg-4 col-xl-4">
                  <div>
                    <input
                      type="text"
                      className="form-control v-space"
                      id="search"
                      placeholder="Search"
                      value={searchString}
                      onChange={(e) => setSearchString(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-12 col-lg-3 col-xl-4 d-flex justify-content-between">
                  <button
                    type="submit"
                    className="btn btn-secondary w-50"
                    onClick={handleSearch}
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary w-50 ms-2"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="col-12 col-lg-2">
            <div className="dropdown">
              <button
                className="btn btn-secondary dropdown-toggle w-100 py-4"
                type="button"
                id="dateFilterDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bi bi-calendar me-2"></i>Date Filter
              </button>
              <ul
                className="dropdown-menu p-3"
                aria-labelledby="dateFilterDropdown"
              >
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handlePresetSelect("this-week")}
                  >
                    This Week
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handlePresetSelect("last-week")}
                  >
                    Last Week
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handlePresetSelect("this-month")}
                  >
                    This Month
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => handlePresetSelect("last-month")}
                  >
                    Last Month
                  </button>
                </li>
                <hr className="my-2" />
                <li className="px-2">
                  <div className="mb-2">
                    <label className="form-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={tempFromDate}
                      onChange={(e) => handleTempDateChange(e, "from")}
                      onBlur={() => handleDateSelection("from")}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={tempToDate}
                      onChange={(e) => handleTempDateChange(e, "to")}
                      onBlur={() => handleDateSelection("to")}
                    />
                  </div>
                  <div className="mt-3">
                    <button
                      className="btn btn-secondary w-100"
                      onClick={handleClearDates}
                    >
                      Clear Dates
                    </button>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="row mb-3 clearfix">
          <div className="col-12 col-xl-12">
            <div className="table-container">
              {loading ? (
                <div className="text-center mt-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : emails.length > 0 ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Email template name</th>
                      <th scope="col">Type</th>
                      <th scope="col">Created by name</th>
                      <th scope="col">Created date</th>
                      <th scope="col">Status</th>
                      <th scope="col">Action items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emails.map((email, index) => (
                      <tr key={email._id}>
                        <th scope="row">
                          {(currentPage - 1) * emailsPerPage + index + 1}
                        </th>
                        <td>{email.emailName}</td>
                        <td>
                          <span className={`badge ${
                            email.emailName && email.emailName.includes('E&S') ? 'bg-warning text-dark' : 'bg-primary'
                          }`}>
                            {email.emailName && email.emailName.includes('E&S') ? 'E&S' : 'Standard'}
                          </span>
                        </td>
                        <td>{email.userName}</td>
                        <td>
                          {new Date(email.createdAt).toLocaleDateString()}
                        </td>
                        <td>{email.status}</td>
                        <td>
                          <a href="#" onClick={handleViewEmail(email._id)}>
                            <i className="bi bi-eye me-2"></i>
                          </a>
                          {email.status !== "Sent" &&
                            email.status !== "In Outlook" && (
                              <i
                                className="bi bi-trash"
                                onClick={() => handleDeleteEmail(email._id)}
                              ></i>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No emails to display</p>
              )}
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <button
            className="btn btn-outline-secondary"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-outline-secondary"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </>
    );
  }
}

export default ManageEmails;
